<template>
  <div class="game-settings-container">
    <h1 class="game-title">{{ $t('PvPgame.PvPgameTitle') }}</h1>
    <div class="setting-section">
      <div class="setting-title">{{ $t('PvPgame.topic') }}</div>
      <div class="vocabulary-counter">
        {{ topic.length }}/20
      </div>
      <input class="input-text" type="text" v-model="topic" :placeholder="$t('PvPgame.inputTopic')" maxlength="20">
    </div>
  
    <div class="setting-section correct-section">
      <div class="setting-title">{{ $t('PvPgame.correctItems') }}</div>
      <div class="vocabulary-counter">
        {{ vocabularyList.length }}/20
      </div>
      <div class="vocabulary-input">
        <textarea v-model="vocabularyInput" :placeholder="$t('PvPgame.correctItemsPlaceholder')"
          @keydown="handleKeyDown"></textarea>
      </div>

      <div class="vocabulary-tags">
        <div v-for="(word, index) in vocabularyList" :key="index" class="vocabulary-tag">
          {{ word }}
          <span class="delete-tag" @click="removeVocabulary(index)">×</span>
        </div>
      </div>


    </div>


    <div class="setting-section error-section">
      <div class="setting-title">{{ $t('PvPgame.errorItems') }}</div>
      <div class="vocabulary-counter">
        {{ errorVocabularyList.length }}/20
      </div>
      <div class="vocabulary-input">
        <textarea v-model="errorVocabularyInput" :placeholder="$t('PvPgame.errorItemsPlaceholder')"
          @keydown="handleErrorKeyDown"></textarea>
      </div>

      <div class="vocabulary-tags error-tags">
        <div v-for="(word, index) in errorVocabularyList" :key="index" class="vocabulary-tag error-tag">
          {{ word }}
          <span class="delete-tag" @click="removeErrorVocabulary(index)">×</span>
        </div>
      </div>
    </div>

    <div class="setting-section">
      <div class="setting-title">{{ $t('PvPgame.gameDuration') }}</div>
      <div class="duration-selector">
        <select v-model="gameDuration">
          <option v-for="option in durationOptions" :key="option" :value="option">
            {{ option }} {{ $t('PvPgame.second') }}
          </option>
        </select>
      </div>
    </div>
    <div class="setting-section">
      <div class="setting-title">{{ $t('PvPgame.gameSpeed') }}</div>
      <div class="duration-selector">
        <select v-model="gameSpeed">
          <option v-for="option in speedOptions" :key="option" :value="option">
            {{ getSpeedText(option) }}
          </option>
        </select>
      </div>
    </div>

    <div class="setting-section">
      <div class="setting-title">{{ $t('PvPgame.backgroundSetting') }}</div>
      <div class="background-selector">
        <div 
          v-for="option in backgroundData" 
          :key="option.value" 
          class="background-option" 
          :class="{ 'selected': background === option.value }"
          @click="background = option.value"
        >
          <img :src="option.image" />
          <div class="background-label">{{ $t('PvPgame.'+option.value) }}</div>
        </div>
      </div>
    </div>

    <div class="action-buttons">
      <button class="clear-button" @click="clearData">{{ $t('PvPgame.clearData') }}</button>
      <button class="start-button" @click="startGame" :disabled="!canStartGame">{{ $t('PvPgame.saveSettings') }}</button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, inject, getCurrentInstance, onMounted } from 'vue';
import { useRouter } from 'vue-router';

const router = useRouter();
const that = getCurrentInstance().proxy;

// 通信工具類
const MessageBridge = {
    // 環境類型
    ENV: {
        WEBVIEW: 'webview',
        WEB: 'web'
    },
    // 當前環境
    currentEnv: 'webview',
    // 初始化
    init() {
        this.currentEnv = window?.chrome?.webview ? this.ENV.WEBVIEW : this.ENV.WEB;
        return this;
    },
    // 發送消息
    postMessage(data, origin = '*') {
        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.postMessage(data);
        } else {
            window.postMessage(data, origin);
        }
    },
    // 添加消息監聽器
    addEventListener(callback) {
        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.addEventListener('message', event => {
                callback(event);
            });
        } else {
            window.addEventListener('message', event => {
                callback(event);
            });
        }
    }
};

// 初始化消息橋接器
const messageBridge = MessageBridge.init();

onMounted(() => {
  messageBridge.addEventListener(event => {
    const msg = event.data;
    console.log(msg, 'msg');
  });
  
  // 從 sessionStorage 讀取配置資料
  const savedConfig = sessionStorage.getItem('PvPGameConfig');
  if (savedConfig) {
    try {
      const config = JSON.parse(savedConfig);
      console.log('從 sessionStorage 讀取到的配置:', config);
      
      // 填充表單欄位
      if (config.topic) topic.value = config.topic;
      if (config.correctVocabularyList && Array.isArray(config.correctVocabularyList)) {
        vocabularyList.value = [...config.correctVocabularyList];
      }
      if (config.errorVocabularyList && Array.isArray(config.errorVocabularyList)) {
        errorVocabularyList.value = [...config.errorVocabularyList];
      }
      if (config.gameDuration) gameDuration.value = config.gameDuration;
      if (config.background) background.value = config.background;
      if (config.gameSpeed) gameSpeed.value = config.gameSpeed;
      
    } catch (error) {
      console.error('解析 sessionStorage 中的配置時出錯:', error);
    }
  }
});

const $Message = inject("$Message");

const topic = ref('');

// 正確詞彙列表
const vocabularyInput = ref('');
const vocabularyList = ref([]);

// 錯誤詞彙列表
const errorVocabularyInput = ref('');
const errorVocabularyList = ref([]);

// 遊戲時長選項
const durationOptions = [20, 30, 40];
const gameDuration = ref(30);

// 遊戲速度選項
const speedOptions = [3, 2, 1];
const gameSpeed = ref(2); // 預設中速


// 計算是否可以開始遊戲
const canStartGame = computed(() => vocabularyList.value.length > 0 && errorVocabularyList.value.length > 0);

// 根據速度值獲取對應的語系文字
const getSpeedText = (speed) => {
  if (speed === 3) return that.$t('PvPgame.speedFast');
  if (speed === 2) return that.$t('PvPgame.speedMedium');
  if (speed === 1) return that.$t('PvPgame.speedSlow');
  return speed;
};

// 處理正確詞彙的按鍵事件
const handleKeyDown = (event) => {
  // 如果按下Enter，則處理所有逗號分隔的詞彙
  if (event.key === 'Enter') {
    event.preventDefault();
    addVocabulary();
  }
  // 如果按下逗號，只在輸入框中添加逗號，不進行處理
  // 這樣用戶可以輸入多個詞彙，用逗號分隔，然後按Enter一次性添加
};

// 處理錯誤詞彙的按鍵事件
const handleErrorKeyDown = (event) => {
  // 如果按下Enter，則處理所有逗號分隔的詞彙
  if (event.key === 'Enter') {
    event.preventDefault();
    addErrorVocabulary();
  }
};

// 新增正確詞彙
const addVocabulary = () => {
  // 分割輸入的文本，使用中英文逗號作為分隔符
  const words = vocabularyInput.value.split(/[,，]+/).filter(word => word.trim() !== '');

  // 添加所有有效的詞彙
  for (const word of words) {

    if (vocabularyList.value.includes(word.trim())) {
      $Message.warning({
        content: that.$t('PvPgame.wordExists'),
        timeout: 2000,
      });
    }
    if (vocabularyList.value.length < 20 && !vocabularyList.value.includes(word.trim())) {
      vocabularyList.value.push(word.trim());
    }

  }
  if (vocabularyList.value.length == 20) {
    $Message.warning({
      content: that.$t('PvPgame.maxVocabulary'),
      timeout: 2000,
    });
  }
  // 清空輸入框
  vocabularyInput.value = '';
};

// 新增錯誤詞彙
const addErrorVocabulary = () => {
  // 分割輸入的文本，使用中英文逗號作為分隔符
  const words = errorVocabularyInput.value.split(/[,，]+/).filter(word => word.trim() !== '');

  // 添加所有有效的詞彙
  for (const word of words) {
    if (errorVocabularyList.value.includes(word.trim())) {
      $Message.warning({
        content: that.$t('PvPgame.wordExists'),
        timeout: 2000,
      });
    }
    if (errorVocabularyList.value.length < 20 && !errorVocabularyList.value.includes(word.trim())) {
      errorVocabularyList.value.push(word.trim());
    }

  }
  if (errorVocabularyList.value.length == 20) {
    $Message.warning({
      content: that.$t('PvPgame.maxErrorVocabulary'),
      timeout: 2000,
    });
  }
  // 清空輸入框
  errorVocabularyInput.value = '';
};

// 移除正確詞彙
const removeVocabulary = (index) => {
  vocabularyList.value.splice(index, 1);
};

// 移除錯誤詞彙
const removeErrorVocabulary = (index) => {
  errorVocabularyList.value.splice(index, 1);
};

// 清空設定數據
const clearData = () => {
  topic.value = '';
  vocabularyList.value = [];
  vocabularyInput.value = '';
  errorVocabularyList.value = [];
  errorVocabularyInput.value = '';
  gameDuration.value = 30;
  gameSpeed.value = 2; // 預設中速
  background.value = 'ocean';
  
  // 清除 sessionStorage 中的配置
  sessionStorage.removeItem('PvPGameConfig');
  
};

// 開始遊戲
const startGame = () => {
  const gameSettings = {
    topic: topic.value,
    correctVocabularyList: JSON.parse(JSON.stringify(vocabularyList.value)),
    errorVocabularyList: JSON.parse(JSON.stringify(errorVocabularyList.value)),
    gameDuration: gameDuration.value,
    background: background.value,
    gameSpeed: gameSpeed.value
  };

  console.log('遊戲設定:', gameSettings);
  // 這裡可以添加導航到遊戲頁面或發送設定到父組件的邏輯
  messageBridge.postMessage({
    action: 'Save.PvPGameSetting',
    data: gameSettings
  });
  router.push('/ht-pvpgame-play');
};


// 背景選項資料
const backgroundData = [
  { value: 'ocean', image: require('@/assets/HTwbActAssets/ocean_world.webp') },
  { value: 'desert', image: require('@/assets/HTwbActAssets/desert_oasis.webp') },
  { value: 'forest', image: require('@/assets/HTwbActAssets/fantasy_forest.webp') }
];

const background = ref('ocean');


</script>

<style lang="less" scoped>
.game-settings-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 10px 0px 20px 0px;
}

.game-title {
    font-size: 2rem;
    text-align: center;
  }

.setting-section {
  position: relative;
  margin-bottom: 10px;
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 20px;

  .setting-title {
    font-size: 1.2rem;
    margin-bottom: 10px;
  }
  .input-text{
    width: 100%;
    height: 40px;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-family: inherit;
    margin-bottom: 10px;
  }
  &.correct-section{
    background-color: #e8f4ff;
    padding: 20px;
    .setting-title {
      color: blue;
    }
  }

  &.error-section {
    background-color: #fff0f3;

    .setting-title {
      color: #d81b60;
    }
  }
}

.vocabulary-input {
  textarea {
    width: 100%;
    height: 100px;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    resize: vertical;
    font-family: inherit;
    margin-bottom: 10px;
  }
}

.vocabulary-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;;
  margin-bottom: 10px;

  .vocabulary-tag {
    background-color: #e8f4ff;
    border: 1px solid #b8dcff;
    border-radius: 20px;
    padding: 5px 10px;
    display: flex;
    align-items: center;
    font-size: 0.9rem;

    .delete-tag {
      margin-left: 5px;
      cursor: pointer;
      font-weight: bold;
      color: #666;

      &:hover {
        color: #ff4d4d;
      }
    }

    &.error-tag {
      background-color: #ffe0e9;
      border: 1px solid #ffb6c9;

      .delete-tag {
        &:hover {
          color: #d81b60;
        }
      }
    }
  }
}

.vocabulary-counter {
  position: absolute;
  right: 22px;
  top: 25px;
  font-size: 0.9rem;
  color: #666;
  font-weight: bold;
}

.duration-selector {
  select {
    width: 100%;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-family: inherit;
    font-size: 1rem;
  }
}

.background-selector {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 10px;
  
  .background-option {
    width: 150px;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    border: 3px solid transparent;
    
    img {
      width: 100%;
      height: 100px;
      object-fit: cover;
      display: block;
    }
    
    .background-label {
      padding: 8px;
      text-align: center;
      background-color: #f5f5f5;
      font-size: 0.9rem;
    }
    
    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
    }
    
    &.selected {
      border-color: #4caf50;
      
      &::after {
        content: '✓';
        position: absolute;
        top: 5px;
        right: 5px;
        width: 25px;
        height: 25px;
        background-color: #4caf50;
        color: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
      }
      
      .background-label {
        background-color: #e8f5e9;
        color: #2e7d32;
        font-weight: bold;
      }
    }
  }
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 15px;

  .clear-button {
    background-color: #f44336;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 5px 10px;
    font-size: 1.1rem;
    cursor: pointer;
    font-family: inherit;
    transition: background-color 0.2s;

    &:hover {
      background-color: #da190b;
    }
  }

  .start-button {
    background-color: #4caf50;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 5px 10px;
    font-size: 1.1rem;
    cursor: pointer;
    font-family: inherit;
    transition: background-color 0.2s;

    &:hover {
      background-color: #45a049;
    }

    &:disabled {
      background-color: #cccccc;
      cursor: not-allowed;
    }
  }
}
</style>